import { FormControl, Grid, TextField, FormControlLabel, withStyles, Checkbox } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useForm } from "hooks/useForm";
import { useRuleValidator } from "hooks/useRuleValidator";
import { PERMISSIONS } from "router/permissions";
import { updateCustomerCreditConditions } from "redux/actions/customers/customersAction";
import RegularButton from "components/CustomButtons/Button";
import { CustomerCreditConditionsViewAlert } from "./CustomerCreditConditionsViewAlert";

const CustomFormControlLabel = withStyles({
  root: {
    display: 'flex',
    justifyContent: 'space-between'
  },
  label: {
    margin: 0,
    color: "grey"
  }
})(FormControlLabel);

const MAX_CREDIT_DAYS = 120;

export const CustomerCreditConditionsForm = () => {
  const dispatch = useDispatch();
  const { code } = useParams();
  const { customer, customerConditions } = useSelector(({ customersReducer }) => customersReducer);
  const { isLoadingComp } = useSelector(({ search }) => search);
  const [isCreditNote, setIsCreditNote] = useState(!!customerConditions?.isCreditNote)
  const [creditStatus, setCreditStatus] = useState(true)

  // Una sola regla de permiso gobierna toda la sección "Condiciones de Crédito".
  const canEdit = useRuleValidator(PERMISSIONS.CUSTOMER_CREDIT_CONDITIONS_EDIT);

  // Cliente de crédito: isCredit === 1 (mismo criterio usado en CustomerInfo.jsx).
  // Cualquier otro valor (0, null, undefined) se trata como cliente de contado.
  const isCreditCustomer = customer?.isCredit === 1;

  // Código ERP: mismo criterio usado en CustomerView.jsx para detectar cuentas
  // vinculadas a un ERP (código con prefijo "ERP-"). Se toma de la ruta y no
  // de `account` del store para que sea correcto desde el primer render.
  const hasErpCode = !!code?.includes("ERP-");

  // Regla 3: cliente de crédito con código ERP -> sección totalmente de solo
  // lectura, sin importar el permiso del usuario.
  const isReadOnlyByErpCredit = isCreditCustomer && hasErpCode;

  // El estado inicial del store no trae `credit`: si sigue en undefined, el GET
  // no ha resuelto o falló. Sin datos la sección queda de solo lectura, porque
  // el PUT sobrescribe todos los campos y mandaría 0 en lugar de los reales.
  const hasConditionsData = customerConditions?.credit !== undefined;

  // Regla única: con el permiso, toda la sección es editable -incluido el
  // descuento-, salvo el caso anterior. "Cliente de contado" era el ejemplo
  // de negocio para ilustrar la regla, no una condición aparte para ese campo.
  const canEditGeneralFields = canEdit && !isReadOnlyByErpCredit && hasConditionsData;

  const showEditControls = canEditGeneralFields;

  // Fuente de datos: GET /crm/customers/:id/customers-conditions (ya se
  // despacha desde CustomerView al entrar al cliente). La API expone el
  // consumo acordado como "volume" en la respuesta; aquí se conserva el
  // nombre de campo/label "Consumo Mensual" que ya usaba el formulario.
  const initialValues = {
    credit: customerConditions?.credit ?? "",
    creditDays: customerConditions?.creditDays ?? "",
    discount: customerConditions?.discount ?? "",
    monthlyConsumption: customerConditions?.volume ?? "",
    maxMonthlyConsumption: customerConditions?.maxMonthlyConsumption ?? "",
  };

  const {
    inputValues,
    handleNewValues,
    cleanForm,
  } = useForm(initialValues);

  const {
    credit,
    creditDays,
    discount,
    monthlyConsumption,
    maxMonthlyConsumption,
  } = inputValues;

  // useState/useForm solo leen su valor inicial al montar: si el GET de
  // condiciones resuelve después (o se cambia de cliente), se re-sincroniza
  // el form con lo que haya en el store.
  useEffect(() => {
    handleNewValues(initialValues);
    setIsCreditNote(!!customerConditions?.isCreditNote);
    //eslint-disable-next-line
  }, [customerConditions]);

  const handleInputOnChange = ({target}) => {
    const {name, value, min, max} = target
    let newValue = value
    if(value) {
      if (min && parseFloat(value) < parseFloat(min)) newValue = min
      if (max && parseFloat(value) > parseFloat(max)) newValue = max
      if (name.toLowerCase().includes('days')) newValue = parseInt(newValue)
    }
    handleNewValues({
      ...inputValues,
      [name]: newValue
    })
  }

  const handleCancel = () => {
    cleanForm();
    setIsCreditNote(!!customerConditions?.isCreditNote);
  }

  // PUT /crm/customers/:id/customers-conditions exige los 5 campos; los que
  // no tengan valor se mandan en 0 (isCreditNote en false). El nombre que
  // pide el backend para el consumo acordado es "agreedVolume".
  const handleSubmit = () => {
    if (!canEditGeneralFields) return;
    const form = {
      credit: Number(credit) || 0,
      creditDays: Math.min(Number(creditDays) || 0, MAX_CREDIT_DAYS),
      discount: Number(discount) || 0,
      agreedVolume: Number(monthlyConsumption) || 0,
      maxMonthlyConsumption: Number(maxMonthlyConsumption) || 0,
      isCreditNote: !!isCreditNote,
    };
    dispatch(updateCustomerCreditConditions(customer.id, form));
  }

  return (
    <FormControl fullWidth>
      <Grid container spacing={1}>
        { creditStatus && <CustomerCreditConditionsViewAlert setCreditStatusAlert={setCreditStatus}/>}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Limite de Credito"
            name="credit"
            type="number"
            value={credit}
            onChange={handleInputOnChange}
            variant="outlined"
            InputProps={{ endAdornment: <p style={{ margin: 0 }}>$</p>,  inputProps: { min: 0 } }}
            disabled={!canEditGeneralFields}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Días de Credito"
            name="creditDays"
            type="number"
            value={creditDays}
            onChange={handleInputOnChange}
            variant="outlined"
            InputProps={{ inputProps: { min: 0, max: MAX_CREDIT_DAYS } }}
            disabled={!canEditGeneralFields}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Descuento"
            name="discount"
            type="number"
            value={discount}
            onChange={handleInputOnChange}
            variant="outlined"
            InputProps={{ endAdornment: <p style={{ margin: 0 }}>$</p>, inputProps: { min: 0 } }}
            disabled={!canEditGeneralFields}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Consumo Mensual"
            name="monthlyConsumption"
            type="number"
            value={monthlyConsumption}
            onChange={handleInputOnChange}
            variant="outlined"
            InputProps={{ endAdornment: <p style={{ margin: 0 }}>$</p>, inputProps: { min: 0 } }}
            disabled={!canEditGeneralFields}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CustomFormControlLabel
            control={
              <Checkbox
                name="isCreditNote"
                onChange={() => setIsCreditNote(!isCreditNote)}
                checked={isCreditNote}
                disabled={!canEditGeneralFields}
                color='primary'
              />
            }
            labelPlacement="start"
            label="Nota de Credito"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Consumo Máximo Mensual"
            name="maxMonthlyConsumption"
            type="number"
            value={maxMonthlyConsumption}
            onChange={handleInputOnChange}
            variant="outlined"
            InputProps={{ endAdornment: <p style={{ margin: 0 }}>$</p>, inputProps: { min: 0 } }}
            disabled={!canEditGeneralFields}
          />
        </Grid>
        {showEditControls && (
          <Grid
            item
            xs={12}
            style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}
          >
            <RegularButton
              color="danger"
              onClick={handleCancel}
              loading={isLoadingComp?.loadingCustomerCrud}
            >
              Cancelar
            </RegularButton>
            <RegularButton
              color="info"
              onClick={handleSubmit}
              loading={isLoadingComp?.loadingCustomerCrud}
            >
              Guardar
            </RegularButton>
          </Grid>
        )}
      </Grid>
    </FormControl>
  );
};
